import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listJournalEntries, saveJournalEntry, deleteJournalEntry } from "@/lib/academy.functions";
import { AcademyNav } from "@/components/academy/AcademyNav";
import { NotebookPen, Trash2, Save } from "lucide-react";
import ReactMarkdown from "react-markdown";

export const Route = createFileRoute("/_authenticated/academy/journal")({
  head: () => ({ meta: [{ title: "Journal · Academy" }, { name: "robots", content: "noindex" }] }),
  component: JournalPage,
});

function JournalPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{ id?: string; title: string; body: string; tags: string }>({ title: "", body: "", tags: "" });

  const entries = useQuery({
    queryKey: ["academy", "journal", q],
    queryFn: () => listJournalEntries({ data: { q } }),
  });

  const save = useMutation({
    mutationFn: () => saveJournalEntry({ data: {
      id: editing.id, title: editing.title || undefined, body: editing.body,
      tags: editing.tags.split(",").map((t) => t.trim()).filter(Boolean),
    } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["academy", "journal"] });
      setEditing({ title: "", body: "", tags: "" });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteJournalEntry({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["academy", "journal"] }),
  });

  return (
    <>
      <AcademyNav />
      <div className="min-h-dvh bg-obsidian text-foreground">
        <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold flex items-center gap-2"><NotebookPen size={14}/> Journal</p>
          <h1 className="mt-2 font-serif text-3xl text-gold-luminous">Private reflections.</h1>

          <div className="mt-6 grid gap-6 lg:grid-cols-[360px_1fr]">
            <aside>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search entries…"
                className="w-full rounded-full border hairline bg-black/40 px-4 py-2 text-sm outline-none focus:border-gold" />
              <ul className="mt-3 space-y-2">
                {(entries.data ?? []).map((e) => (
                  <li key={e.id}>
                    <button onClick={() => setEditing({ id: e.id, title: e.title ?? "", body: e.body_md ?? "", tags: (e.tags ?? []).join(", ") })}
                      className="w-full rounded-xl border hairline p-3 text-left text-sm hover:border-gold/60">
                      <p className="font-serif text-base">{e.title || "Untitled"}</p>
                      <p className="mt-1 line-clamp-2 text-xs text-foreground/60">{e.body_md}</p>
                      <p className="mt-2 text-[10px] uppercase tracking-widest text-foreground/40">{new Date(e.updated_at).toLocaleDateString()}</p>
                    </button>
                  </li>
                ))}
                {(entries.data ?? []).length === 0 && <li className="text-sm text-foreground/60">No entries yet.</li>}
              </ul>
            </aside>

            <div className="rounded-2xl border hairline p-4">
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="Title (optional)"
                className="w-full bg-transparent font-serif text-2xl outline-none placeholder:text-foreground/40" />
              <textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                placeholder="Write markdown…" rows={14}
                className="mt-3 w-full resize-y bg-transparent text-sm outline-none placeholder:text-foreground/40" />
              <input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })}
                placeholder="tags, comma, separated"
                className="mt-3 w-full rounded-full border hairline bg-black/40 px-4 py-2 text-xs outline-none" />

              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => save.mutate()} disabled={!editing.body.trim() || save.isPending}
                  className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-4 py-2 text-sm font-semibold text-obsidian disabled:opacity-50">
                  <Save size={14} /> {editing.id ? "Update" : "Save"}
                </button>
                {editing.id && (
                  <button onClick={() => { if (confirm("Delete this entry?")) del.mutate(editing.id!); setEditing({ title: "", body: "", tags: "" }); }}
                    className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-sm text-red-300">
                    <Trash2 size={14} /> Delete
                  </button>
                )}
                <button onClick={() => setEditing({ title: "", body: "", tags: "" })}
                  className="inline-flex items-center rounded-full border hairline px-4 py-2 text-sm">
                  New
                </button>
              </div>

              {editing.body && (
                <div className="mt-6 rounded-xl bg-black/40 p-4">
                  <p className="mb-2 text-[10px] uppercase tracking-widest text-foreground/50">Preview</p>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <ReactMarkdown>{editing.body}</ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
